/**
 * Error classification — sorting a message into one of four coarse buckets.
 *
 * The buckets exist so the error charts can answer "is this a spike in
 * network failures or a spike in validation failures?" without a human
 * reading messages. They are deliberately coarse: a finer taxonomy would
 * duplicate what Cloud Error Reporting already does better, since it groups
 * on the stack trace rather than on keywords in a string.
 *
 * Keyword matching is a heuristic and will mislabel some messages. That is
 * an accepted cost — the alternative is a category argument at every call
 * site, which drifts from reality the moment someone copies a call.
 *
 * @module logging/core/classify
 */

import type { ErrorCategory } from './types';

/** Checked in order; first match wins. Network is first because its
 *  vocabulary is the most distinctive, and 'unauthorized' would otherwise
 *  be claimed by a broader pattern. */
const PATTERNS: ReadonlyArray<readonly [ErrorCategory, readonly string[]]> = [
  ['network', ['network', 'fetch', 'timeout', 'cors', 'offline', 'econnrefused']],
  ['auth', ['auth', 'permission', 'unauthorized', 'unauthenticated', 'forbidden', 'app check']],
  ['validation', ['validation', 'invalid', 'schema', 'required', 'malformed']],
];

/** Bucket a message. Always returns a category; never throws. */
export function classifyError(message: string): ErrorCategory {
  const haystack = message.toLowerCase();
  for (const [category, needles] of PATTERNS) {
    if (needles.some((needle) => haystack.includes(needle))) return category;
  }
  return 'unknown';
}

/**
 * Reduce anything a caller threw into a readable one-line message.
 *
 * Call sites throw `Error`s, strings, plain objects from rejected promises,
 * and occasionally `undefined`. All of them have to become a string without
 * this function itself throwing — it runs inside error handling, where a
 * second failure is the one nobody can diagnose.
 */
export function describeError(value: unknown): string {
  if (value instanceof Error) return value.message || value.name;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value === null || value === undefined) return 'unknown_error';

  try {
    const asRecord = value as { message?: unknown };
    if (typeof asRecord.message === 'string') return asRecord.message;
    return JSON.stringify(value) ?? 'unknown_error';
  } catch {
    // Circular structures, exotic proxies, getters that throw.
    return 'unserializable_error';
  }
}
